/**
 * Authentication API Endpoints
 * Sign in and sign up endpoints
 */

import apiClient from '../client';
import type { AuthResponse } from '../types';

export interface SignInParams {
  login: string; // email or phone number
  password: string;
  client_id: string;
  client_secret: string;
  country_calling_code?: string;
}

export interface SignUpParams {
  login: string; // email or phone number
  password: string;
  client_id: string;
  client_secret: string;
  country_calling_code?: string;
}

export interface MagicLinkParams {
  email: string;
  first_name?: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
  is_new_user: boolean;
}

export interface GoogleAuthUrlResponse {
  auth_url: string;
  state: string;
}

export const authApi = {
  /**
   * POST /auth/sign_in
   * Sign in and get access token
   */
  signIn: async (params: SignInParams): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/sign_in', params);
    return response.data;
  },

  /**
   * POST /users/sign_up
   * Sign up new user and get access token
   */
  signUp: async (params: SignUpParams): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/users/sign_up', params);
    return response.data;
  },

  /**
   * POST /auth/magic_link
   * Request magic link for passwordless signup/signin
   */
  requestMagicLink: async (params: MagicLinkParams): Promise<MagicLinkResponse> => {
    const response = await apiClient.post<MagicLinkResponse>('/auth/magic_link', params);
    return response.data;
  },

  /**
   * POST /password_reset/request
   * Request password reset email with link
   */
  requestPasswordReset: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/password_reset/request',
      { email }
    );
    return response.data;
  },

  /**
   * GET /auth/google
   * Get Google OAuth authorization URL
   */
  getGoogleAuthUrl: async (): Promise<GoogleAuthUrlResponse> => {
    const response = await apiClient.get<GoogleAuthUrlResponse>('/auth/google');
    return response.data;
  },
};

